import { useEffect, useState } from 'react';
import { IFetchReplyCommentParams, IReplyComment } from '../../models';
import { postService } from '../../services/post.service';
import _ from 'lodash';
import {
  fetchFollowingEmptyPages,
  nextCommentCursor,
} from '../../utils/comment-paging';

export const useReplyComment = ({
  pageNumber,
  visible,
  commentId,
}: {
  pageNumber: number;
  visible: boolean;
  commentId: string;
}) => {
  const [replyComments, setReplyComments] = useState<IReplyComment[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [pagingState, setPagingState] = useState<string>('');

  const getReplyCommentWhenReply = async (commentId: string) => {
    try {
      setLoading(true);
      const {
        data,
        hasMore,
        pagingState: pagingStateResponse,
      } = await postService.getReplyCommentByCommentId({
        pageNumber: 1,
        pageSize: 500,
        pagingState: '',
        commentId,
      });

      setReplyComments(data);
      setHasMore(hasMore && nextCommentCursor(pagingStateResponse) !== null);
      setPagingState(pagingStateResponse);
    } catch (error) {
      console.log('Error fetching reply comments:', error);
    } finally {
      setLoading(false);
    }
  };

  const getReplyComment = async (payload: IFetchReplyCommentParams) => {
    try {
      setLoading(true);
      const {
        data,
        hasMore,
        pagingState: pagingStateResponse,
      } = await fetchFollowingEmptyPages<IReplyComment>(
        (cursor) =>
          postService.getReplyCommentByCommentId({
            ...payload,
            pagingState: cursor,
          }),
        pagingState
      );

      setReplyComments((prev) => _.uniqBy([...prev, ...data], 'id'));
      setHasMore(hasMore);
      setPagingState(pagingStateResponse);
    } catch (error) {
      console.error('Error fetching reply comments:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!visible) {
      setReplyComments([]);
      setPagingState('');
      return;
    }

    const payload: IFetchReplyCommentParams = {
      pageNumber,
      pageSize: 5,
      pagingState: '',
      commentId,
    };
    getReplyComment(payload);
  }, [pageNumber, visible]);

  return {
    data: replyComments,
    loading,
    hasMore,
    getReplyCommentWhenReply,
    getReplyComment,
  };
};
